import { computed, shallowRef } from 'vue'
import { defaultReportError } from '@/composables/useErrorLogger'
import { useItinerarySDK } from './useItinerarySDK'

/** GCOME_AP 轴体类型 SDK 封装。 */
export const useShaftType = () => {
  const { getKeyTravelFeature } = useItinerarySDK()
  const shaftTypeMap = shallowRef(new Map<number, number>())
  const selectedKeys = shallowRef<Array<{ index: number }>>([])
  const selectedShaftType = shallowRef<number | null>(null)
  const canSaveShaftType = computed(
    () => selectedKeys.value.length > 0 && selectedShaftType.value !== null,
  )
  let loadToken = 0

  /** 读取全部键位的轴体类型。 */
  const getKeyShaftTypes = async () => {
    const feature = getKeyTravelFeature()
    if (!feature) return
    try {
      return await feature.getKeyShaftTypes()
    } catch (error) {
      console.error('Failed to get key shaft types', error)
      defaultReportError(`Failed to get key shaft types: ${JSON.stringify(error)}`)
      throw error
    }
  }

  /** 批量设置键位的轴体类型。 */
  const setKeyShaftTypes = async (entries: Array<{ index: number; shaftType: number }>) => {
    const feature = getKeyTravelFeature()
    if (!feature) return
    try {
      await feature.setKeyShaftTypes(entries)
    } catch (error) {
      console.error('Failed to set key shaft types', error)
      defaultReportError(`Failed to set key shaft types: ${JSON.stringify(error)}`)
      throw error
    }
  }

  /** 读取指定键位的轴体类型。 */
  const getKeyShaftType = async (keyIndex: number) => {
    const feature = getKeyTravelFeature()
    if (!feature) return
    try {
      return await feature.getKeyShaftType(keyIndex)
    } catch (error) {
      console.error('Failed to get key shaft type', error)
      defaultReportError(`Failed to get key shaft type: ${JSON.stringify(error)}`)
      throw error
    }
  }

  /** 设置指定键位的轴体类型。 */
  const setKeyShaftType = async (keyIndex: number, shaftType: number) => {
    const feature = getKeyTravelFeature()
    if (!feature) return
    try {
      await feature.setKeyShaftType(keyIndex, shaftType)
    } catch (error) {
      console.error('Failed to set key shaft type', error)
      defaultReportError(`Failed to set key shaft type: ${JSON.stringify(error)}`)
      throw error
    }
  }

  const loadKeyShaftTypes = async () => {
    const token = ++loadToken
    shaftTypeMap.value = new Map()
    try {
      const entries = await getKeyShaftTypes()
      if (token !== loadToken || !entries) return
      shaftTypeMap.value = new Map(entries.map(({ index, shaftType }) => [index, shaftType]))
      if (selectedShaftType.value === null) {
        const first = selectedKeys.value[0]
        selectedShaftType.value = first ? (shaftTypeMap.value.get(first.index) ?? null) : null
      }
    } catch {
      // getKeyShaftTypes 已记录并上报
    }
  }

  const handleSelectionChange = (keys: Array<{ index: number }>) => {
    const previousFirst = selectedKeys.value[0]?.index
    selectedKeys.value = keys
    if (!keys.length) {
      selectedShaftType.value = null
      return
    }

    const first = keys[0]
    if (first.index !== previousFirst) {
      selectedShaftType.value = shaftTypeMap.value.get(first.index) ?? null
    }
  }

  const selectShaftType = (shaftType: number) => {
    if (!selectedKeys.value.length) return
    selectedShaftType.value = shaftType
  }

  const saveSelectedShaftTypes = async () => {
    const shaftType = selectedShaftType.value
    if (shaftType === null || !selectedKeys.value.length || !getKeyTravelFeature()) return false
    try {
      await setKeyShaftTypes(selectedKeys.value.map(({ index }) => ({ index, shaftType })))
      const nextShaftTypeMap = new Map(shaftTypeMap.value)
      for (const { index } of selectedKeys.value) {
        nextShaftTypeMap.set(index, shaftType)
      }
      shaftTypeMap.value = nextShaftTypeMap
      return true
    } catch {
      // setKeyShaftTypes 已记录并上报
      return false
    }
  }

  return {
    shaftTypeMap,
    selectedShaftType,
    canSaveShaftType,
    getKeyShaftTypes,
    setKeyShaftTypes,
    getKeyShaftType,
    setKeyShaftType,
    loadKeyShaftTypes,
    handleSelectionChange,
    selectShaftType,
    saveSelectedShaftTypes,
  }
}
